<template>
  <BaseTemplate app-name="Whatsapp" page-title="">
    <template #content>
      <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-900 px-4 md:px-8 py-4">
        
        <!-- Loading indicator -->
        <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 flex items-center justify-center z-50">
          <div class="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-3">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span class="text-gray-700 dark:text-gray-200">Carregando...</span>
          </div>
        </div>

        <!-- Error message -->
        <div v-if="error" class="max-w-6xl mx-auto mb-4">
          <div class="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded">
            {{ error }}
          </div>
        </div>
        
        <!-- Tela Inicial - Lista de Reuniões -->
        <div v-if="currentView === 'home'" class="w-full">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-6">
            <div class="flex flex-col md:flex-row justify-between items-center mb-6">
              <h1 class="text-3xl font-bold text-gray-800 dark:text-white mb-4 md:mb-0">
                <i class="fas fa-users text-blue-600 dark:text-blue-400 mr-3"></i>
                Gerenciar Eventos
              </h1>
              <button 
            @click="startNewMeeting"
            class="flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-3 rounded-xl font-semibold 
                  hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl 
                  transform hover:-translate-y-1"
          >
            <Plus class="w-5 h-5 mr-2" />
            Novo Evento
          </button>

            </div>
          </div>

          <!-- Lista de Reuniões -->
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
            <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-4">
              <i class="fas fa-history text-indigo-600 dark:text-indigo-400 mr-2"></i>
              Histórico de Eventos
            </h2>
            
            <!-- Abas para Eventos em Andamento e Finalizados -->
            <div class="flex space-x-1 mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button 
                @click="activeTab = 'ongoing'"
                :class="[
                  'flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200',
                  activeTab === 'ongoing' 
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
                ]"
              >
                <i class="fas fa-clock mr-2"></i>
                Em Andamento ({{ ongoingMeetings.length }})
              </button>
              <button 
                @click="activeTab = 'completed'"
                :class="[
                  'flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200',
                  activeTab === 'completed' 
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
                ]"
              >
                <i class="fas fa-check-circle mr-2"></i>
                Finalizadas ({{ completedMeetings.length }})
              </button>
            </div>
            
            <!-- Eventos em Andamento -->
            <div v-if="activeTab === 'ongoing'">
              <div v-if="ongoingMeetings.length === 0" class="text-center py-12">
                <i class="fas fa-clock text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
                <p class="text-gray-500 dark:text-gray-400 text-lg">Nenhum evento em andamento</p>
              </div>
              <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div 
                  v-for="meeting in ongoingMeetings" 
                  :key="meeting.id"
                  class="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4 hover:shadow-lg transition-all duration-300 cursor-pointer"
                  @click="continueMeeting(meeting)"
                >
                  <div class="flex items-center justify-between mb-3">
                    <h3 class="font-bold text-gray-800 dark:text-white truncate">{{ meeting.subject }}</h3>
                    <span class="bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded-full text-xs font-semibold">
                      <i class="fas fa-clock mr-1"></i>
                      Em Andamento
                    </span>
                  </div>
                  <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <i class="fas fa-calendar mr-1"></i>
                    {{ formatDate(meeting.date) }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    <i class="fas fa-users mr-1"></i>
                    {{ meeting.signatures?.length || 0 }} participantes
                  </p>
                  
                  <!-- Lista de pessoas que já assinaram -->
                  <div v-if="meeting.signatures && meeting.signatures.length > 0" class="mb-3">
                    <p class="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Participantes confirmados:</p>
                    <div class="flex flex-wrap gap-1">
                      <span 
                        v-for="signature in meeting.signatures.slice(0, 3)" 
                        :key="signature.id"
                        class="bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-2 py-1 rounded-full text-xs font-medium"
                        :title="`${signature.name} - ${signature.role} (${signature.sector})`"
                      >
                        {{ signature.name.split(' ')[0] }}
                      </span>
                      <span 
                        v-if="meeting.signatures.length > 3" 
                        class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full text-xs font-medium"
                      >
                        +{{ meeting.signatures.length - 3 }}
                      </span>
                    </div>
                  </div>
                  
                  <div class="flex items-center justify-end">
                    <button 
                      @click.stop="continueMeeting(meeting)"
                      class="flex items-center bg-blue-600 dark:bg-blue-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200"
                    >
                      <FolderOpen class="w-4 h-4 mr-1" />
                      Abrir
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Eventos Finalizados -->
            <div v-if="activeTab === 'completed'">
              <div v-if="completedMeetings.length === 0" class="text-center py-12">
                <i class="fas fa-calendar-times text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
                <p class="text-gray-500 dark:text-gray-400 text-lg">Nenhum evento finalizado encontrado</p>
              </div>
              <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div 
                  v-for="meeting in completedMeetings" 
                  :key="meeting.id"
                  class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:shadow-lg transition-all duration-300 cursor-pointer"
                  @click="viewMeeting(meeting)"
                >
                  <div class="flex items-center justify-between mb-3">
                    <h3 class="font-bold text-gray-800 dark:text-white truncate">{{ meeting.title }}</h3>
                    <span class="bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-2 py-1 rounded-full text-xs font-semibold">
                      Concluída
                    </span>
                  </div>
                  <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <i class="fas fa-calendar mr-1"></i>
                    {{ formatDate(meeting.date) }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    <i class="fas fa-users mr-1"></i>
                    {{ meeting.signatures?.length || 0 }} participantes
                  </p>

                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Novo Evento -->
        <div v-if="showNewMeetingModal" class="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-80 backdrop-blur-sm flex items-start md:items-center justify-center z-50 p-3 md:p-4">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 md:p-6 lg:p-8 w-[95%] max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl max-h-[85vh] md:max-h-[90vh] overflow-y-auto mt-8 md:mt-12 lg:mt-16">
              <div class="flex justify-between items-center mb-4 md:mb-6">
                <h2 class="text-lg md:text-xl font-bold text-gray-800 dark:text-white">
                  <i class="fas fa-plus-circle text-blue-600 dark:text-blue-400 mr-2"></i>
                  Novo Evento
                </h2>
                <button @click="cancelNewMeeting" class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1">
                  <i class="fas fa-times text-lg md:text-xl"></i>
                </button>
              </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 lg:gap-6 mb-4 md:mb-6">
              <!-- Novo campo: Título do Evento -->
              <div class="md:col-span-2">
                <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Título do Evento *</label>
                <input 
                  v-model="meetingForm.title"
                  type="text" 
                  placeholder="Digite o título do evento"
                  class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-xs md:text-sm"
                >
              </div>
              
              <div class="md:col-span-2">
                <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Assunto do Evento *</label>
                <textarea 
                  v-model="meetingForm.subject"
                  placeholder="Descreva o assunto principal do evento"
                  rows="2"
                  class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-xs md:text-sm"
                ></textarea>
              </div>
              
              <div class="md:col-span-2">
                <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Instrutor *</label>
                <input 
                  v-model="meetingForm.instructor"
                  type="text" 
                  placeholder="Nome do instrutor/facilitador"
                  class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-xs md:text-sm"
                >
              </div>
              
              <div>
                <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Data *</label>
                <input 
                  v-model="meetingForm.date"
                  type="date" 
                  class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs md:text-sm"
                >
              </div>
              
              <div>
                <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Hora *</label>
                <input 
                  v-model="meetingForm.time"
                  type="time" 
                  class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs md:text-sm"
                >
              </div>
              
              <div>
                <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Local *</label>
                <input 
                  v-model="meetingForm.location"
                  type="text" 
                  placeholder="Ex: Sala de Reuniões A"
                  class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-xs md:text-sm"
                >
              </div>
              
              <div>
                <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Carga Horária</label>
                <input 
                  v-model="meetingForm.workload"
                  type="text" 
                  placeholder="Ex: 2h, 4 horas"
                  class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-xs md:text-sm"
                >
              </div>
            </div>
            
            <div class="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button 
                @click="cancelNewMeeting"
                class="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-300 text-xs md:text-sm"
              >
                Cancelar
              </button>
              <button 
                @click="createMeeting"
                :disabled="!isFormValid || loading"
                class="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 text-white py-2 md:py-3 rounded-lg md:rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-600 dark:hover:to-indigo-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm flex items-center justify-center gap-2"
              >
                <Play class="w-4 h-4" />
                Iniciar Evento
              </button>
            </div>
          </div>
        </div>

        <!-- Fluxo de Assinatura -->
        <div v-if="currentView === 'signing'" class="max-w-4xl mx-auto px-3 md:px-0">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 md:p-6">
            <!-- Header com progresso -->
            <div class="mb-4 md:mb-6">
              <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4">
                <div>
                  <h2 class="text-lg md:text-xl font-bold text-gray-800 dark:text-white break-words">{{ currentMeeting.title || currentMeeting.subject }}</h2>
                  <p class="text-gray-600 dark:text-gray-300 text-xs md:text-sm">{{ currentMeeting.instructor }}</p>
                </div>
                <div class="flex gap-2 lg:gap-3">
                  <!-- Botão Printer com Dropdown -->
                  <div class="relative" data-printer-button>
                    <button 
                      @click="togglePrinterDropdown"
                      class="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-300 flex items-center gap-2"
                    >
                      <Printer class="w-4 h-4" />
                      <span class="hidden sm:inline">Imprimir</span>
                      <i class="fas fa-chevron-down text-xs"></i>
                    </button>
                    
                    <!-- Dropdown do Printer -->
                    <div v-if="showPrinterDropdown" class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-50">
                      <div class="py-1">
                        <button 
                          @click="generateAttendanceListWithClose"
                          :disabled="loading"
                          class="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 flex items-center gap-3"
                        >
                          <Printer class="w-4 h-4 text-green-600 dark:text-green-500" />
                          Lista de Presença
                        </button>
                        <button 
                          @click="generateImageAuthorizationTermWithClose"
                          :disabled="loading"
                          class="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 flex items-center gap-3"
                        >
                          <Printer class="w-4 h-4 text-purple-600 dark:text-purple-500" />
                          Termo de Imagem
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Botão X com Dropdown -->
                  <div class="relative" data-action-button>
                    <button 
                      @click="toggleActionDropdown"
                      class="bg-red-500 dark:bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-600 dark:hover:bg-red-700 transition-colors duration-300 flex items-center gap-2"
                    >
                      <Settings class="w-4 h-4" />
                      <span class="hidden sm:inline">Ações</span>
                      <i class="fas fa-chevron-down text-xs"></i>
                    </button>
                    
                    <!-- Dropdown do X -->
                    <div v-if="showActionDropdown" class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-50">
                      <div class="py-1">
                        <button 
                          @click="openFinishConfirmationModalWithClose"
                          :disabled="loading"
                          class="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 flex items-center gap-3"
                        >
                          <X class="w-4 h-4 text-red-500" />
                          Finalizar Evento
                        </button>
                        <button 
                          @click="closeMeetingWithClose"
                          class="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 flex items-center gap-3"
                        >
                          <Minimize2 class="w-4 h-4 text-gray-500" />
                          Minimizar Evento
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Barra de Progresso (removida se houver apenas 1 etapa) -->
              <div v-if="steps.length > 1" class="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4 mb-4">
                <div 
                  v-for="(step, index) in steps" 
                  :key="index"
                  class="flex items-center"
                >
                  <div 
                    :class="[
                      'w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-xs md:text-sm',
                      currentStep === index ? 'bg-blue-600 dark:bg-blue-500 text-white' : 
                      currentStep > index ? 'bg-green-500 dark:bg-green-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    ]"
                  >
                    <i v-if="currentStep > index" class="fas fa-check"></i>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <span class="ml-2 font-semibold text-gray-700 dark:text-gray-200 text-xs md:text-sm">{{ step.name }}</span>
                  <div v-if="index < steps.length - 1" class="w-8 md:w-12 h-1 bg-gray-300 dark:bg-gray-600 mx-2 md:mx-4 rounded">
                    <div 
                      :class="['h-full rounded transition-all duration-500', currentStep > index ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-300 dark:bg-gray-600']"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Conteúdo do Step Atual -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 md:p-8">
              <!-- Header do Step -->
              <div class="mb-6 md:mb-8">
                <h3 class="text-lg md:text-xl font-bold text-gray-800 dark:text-white mb-2 md:mb-3">
                  <i :class="steps[currentStep].icon" class="mr-2 md:mr-3"></i>
                  {{ steps[currentStep].name }}
                </h3>
                <p class="text-gray-600 dark:text-gray-300 text-xs md:text-sm">{{ steps[currentStep].description }}</p>
              </div>

              <!-- Formulário de Assinatura -->
              <div class="bg-white dark:bg-gray-600 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
                <h4 class="text-xs md:text-sm font-semibold text-gray-800 dark:text-white mb-3 md:mb-4">
                  <i class="fas fa-user-plus mr-2"></i>
                  Dados do Participante
                </h4>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                  <div>
                    <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Nome Completo</label>
                    <input 
                      v-model="currentSignature.name"
                      type="text" 
                      placeholder="Digite seu nome completo"
                      class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-xs md:text-sm"
                    >
                  </div>
                  <div>
                    <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Cargo/Função</label>
                    <input 
                      v-model="currentSignature.role"
                      type="text" 
                      placeholder="Ex: Gerente, Diretor, etc."
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-xs md:text-sm"
                    >
                  </div>
                  <div class="md:col-span-2">
                    <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-1 md:mb-2 text-xs md:text-sm">Setor</label>
                    <input 
                      v-model="currentSignature.sector"
                      type="text" 
                      placeholder="Ex: TI, RH, Financeiro, etc."
                      class="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-xs md:text-sm"
                    >
                  </div>
                </div>

                <!-- Botão para abrir modal de assinatura -->
                <div class="mb-4 md:mb-6">
                  <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-2 md:mb-3 text-xs md:text-sm">Assinatura Digital</label>
                  <button 
                    @click="openSignatureInputModal"
                    :disabled="!currentSignature.name.trim() || !currentSignature.role.trim() || !currentSignature.sector.trim()"
                    class="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-dashed border-gray-300 dark:border-gray-500 rounded-xl bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div class="flex flex-col items-center justify-center py-4 md:py-6">
                      <i class="fas fa-pen text-xl md:text-2xl text-gray-400 dark:text-gray-500 mb-2 md:mb-3"></i>
                      <span class="text-gray-600 dark:text-gray-300 font-medium text-sm md:text-base">Clique para assinar</span>
                      <span class="text-xs text-gray-500 dark:text-gray-400 mt-1 md:mt-2">Toque aqui para abrir o campo de assinatura</span>
                    </div>
                  </button>
                </div>
                
                <!-- Visualização da assinatura atual -->
                <div v-if="currentSignature.signature" class="mb-4 md:mb-6">
                  <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-2 md:mb-3 text-xs md:text-sm">Sua Assinatura:</label>
                  <div class="bg-white border border-gray-300 dark:border-gray-500 rounded-xl p-3 md:p-4">
                    <img 
                      :src="currentSignature.signature" 
                      alt="Assinatura atual" 
                      class="w-full h-20 md:h-24 object-contain border border-gray-200 dark:border-gray-600 rounded-lg"
                    />
                    <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-3">
                      <button 
                        @click="openSignatureInputModal"
                        class="flex-1 bg-blue-600 dark:bg-blue-500 text-white px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200"
                      >
                        <i class="fas fa-edit mr-1 md:mr-2"></i>
                        Alterar Assinatura
                      </button>

                    </div>
                  </div>
                </div>

                <!-- Checkbox para direito de imagem (agora sempre visível na lista de presença) -->
                <div class="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-xl p-3 md:p-5 mb-4 md:mb-6">
                  <div class="flex items-start space-x-3 md:space-x-4">
                    <input 
                      v-model="currentSignature.imageRightsAgreed"
                      type="checkbox" 
                      id="imageRights"
                      class="mt-1 w-5 h-5 md:w-6 md:h-6 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
                    >
                    <label for="imageRights" class="text-xs md:text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                      <strong>Autorização de Uso de Imagem:</strong> Autorizo o uso da minha imagem, voz e nome para fins de divulgação deste evento em materiais promocionais, redes sociais, site institucional e outras mídias, sem ônus e por prazo indeterminado.
                    </label>
                  </div>
                </div>

                <!-- Botões de Ação -->
                <div class="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200 dark:border-gray-500">
                  <button 
                    @click="saveSignature"
                    :disabled="!canSaveSignature"
                    class="flex-1 bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 text-white py-4 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 dark:hover:from-green-700 dark:hover:to-green-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                  >
                    <i class="fas fa-save mr-3"></i>
                    Salvar Assinatura
                  </button>
                  <button 
                    v-if="currentStep < steps.length - 1"
                    @click="nextStep"
                    class="bg-blue-600 dark:bg-blue-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-300 text-lg"
                  >
                    Próximo <i class="fas fa-arrow-right ml-2"></i>
                  </button>
                </div>
              </div>

              <!-- Lista de Assinaturas do Step Atual -->
              <div v-if="getCurrentStepSignatures().length > 0" class="mt-6">
                <div class="flex items-center justify-between mb-3">
                  <h4 class="font-bold text-gray-800 dark:text-white">
                    <i class="fas fa-list mr-2"></i>
                    Assinaturas Coletadas ({{ getCurrentStepSignatures().length }})
                  </h4>

                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div 
                  v-for="signature in getCurrentStepSignatures()" 
                  :key="signature.id"
                  class="bg-green-50 dark:bg-gray-700 border border-green-200 dark:border-gray-600 rounded-lg p-3"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <p class="font-semibold text-gray-800 dark:text-white">{{ signature.name }}</p>
                      <p class="text-sm text-gray-600 dark:text-gray-300">{{ signature.role }}</p>
                      <p class="text-sm text-gray-600 dark:text-gray-300">{{ signature.sector }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatTime(signature.timestamp) }}</p>
                      
                      <!-- Status da autorização de imagem -->
                      <div class="mt-1">
                        <span v-if="signature.imageRightsAgreed" class="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs">
                          <i class="fas fa-camera mr-1"></i>
                          Direito de Imagem Autorizado
                        </span>
                        <span v-else class="bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-2 py-1 rounded text-xs">
                          <i class="fas fa-camera-slash mr-1"></i>
                          Autorização de Imagem Negada
                        </span>
                      </div>
                    </div>
                    <div class="flex flex-col items-center">
                      <i class="fas fa-check-circle text-green-500 dark:text-green-400 text-xl"></i>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Visualização de Evento -->
        <div v-if="currentView === 'viewMeeting'" class="w-full">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl font-bold text-gray-800 dark:text-white">{{ selectedMeeting.subject }}</h2>
              <div class="flex space-x-3">
                <button 
                  @click="generateAttendanceListWithClose"
                  :disabled="loading"
                  class="bg-green-600 dark:bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors duration-300 disabled:opacity-50"
                >
                  <Printer class="w-4 h-4 mr-1 inline" />
                  Gerar PDF
                </button>
                <button 
                  @click="generateImageAuthorizationTermWithClose"
                  :disabled="loading"
                  class="bg-purple-600 dark:bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors duration-300 disabled:opacity-50"
                >
                  <Printer class="w-4 h-4 mr-1 inline" />
                  Gerar Termo de Imagem
                </button>
                <button 
                  @click="currentView = 'home'"
                  class="bg-gray-500 dark:bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors duration-300"
                >
                  <i class="fas fa-arrow-left mr-1"></i>
                  Voltar
                </button>
              </div>
            </div>

            <!-- Informações do Evento -->
            <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 mb-6">
              <h3 class="text-xl font-bold text-gray-800 dark:text-white mb-4">
                <i class="fas fa-info-circle text-blue-600 dark:text-blue-400 mr-2"></i>
                Informações do Evento
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div class="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                  <div class="flex items-center mb-2">
                    <i class="fas fa-file-alt text-blue-600 dark:text-blue-400 mr-2"></i>
                    <span class="font-semibold text-gray-700 dark:text-gray-200">Assunto</span>
                  </div>
                  <p class="text-gray-800 dark:text-white">{{ selectedMeeting.subject }}</p>
                </div>
                <div class="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                  <div class="flex items-center mb-2">
                    <i class="fas fa-calendar text-green-600 dark:text-green-400 mr-2"></i>
                    <span class="font-semibold text-gray-700 dark:text-gray-200">Data e Hora</span>
                  </div>
                  <p class="text-gray-800 dark:text-white">{{ formatDate(selectedMeeting.date) }}</p>
                </div>
                <div class="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                  <div class="flex items-center mb-2">
                    <i class="fas fa-user-tie text-purple-600 dark:text-purple-400 mr-2"></i>
                    <span class="font-semibold text-gray-700 dark:text-gray-200">Instrutor</span>
                  </div>
                  <p class="text-gray-800 dark:text-white">{{ selectedMeeting.instructor }}</p>
                </div>
                <div class="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                  <div class="flex items-center mb-2">
                    <i class="fas fa-map-marker-alt text-red-600 dark:text-red-400 mr-2"></i>
                    <span class="font-semibold text-gray-700 dark:text-gray-200">Local</span>
                  </div>
                  <p class="text-gray-800 dark:text-white">{{ selectedMeeting.location }}</p>
                </div>
                <div v-if="selectedMeeting.workload" class="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                  <div class="flex items-center mb-2">
                    <i class="fas fa-clock text-orange-600 dark:text-orange-400 mr-2"></i>
                    <span class="font-semibold text-gray-700 dark:text-gray-200">Carga Horária</span>
                  </div>
                  <p class="text-gray-800 dark:text-white">{{ selectedMeeting.workload }}</p>
                </div>
                <div class="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                  <div class="flex items-center mb-2">
                    <i class="fas fa-users text-indigo-600 dark:text-indigo-400 mr-2"></i>
                    <span class="font-semibold text-gray-700 dark:text-gray-200">Total de Participantes</span>
                  </div>
                  <p class="text-gray-800 dark:text-white">{{ selectedMeeting.signatures?.length || 0 }} pessoas</p>
                </div>
              </div>
            </div>

            <!-- Lista de Assinaturas por Etapa -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
              <h3 class="font-bold text-gray-800 dark:text-white mb-4 text-xl">
                <i class="fas fa-users mr-2"></i>
                Lista de Presença - {{ selectedMeeting.signatures?.length || 0 }} Participantes
              </h3>
              
              <div v-if="getStepSignatures(selectedMeeting, 0).length === 0" class="text-center py-12">
                <i class="fas fa-users text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
                <p class="text-gray-500 dark:text-gray-400 text-lg">Nenhuma assinatura coletada</p>
              </div>
              
              <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <div 
                  v-for="signature in getStepSignatures(selectedMeeting, 0)" 
                  :key="signature.id"
                  class="bg-white dark:bg-gray-600 rounded-lg p-4 border border-gray-200 dark:border-gray-500"
                >
                  <div class="flex items-center justify-between mb-3">
                    <div class="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                      <span class="text-blue-600 dark:text-blue-200 font-bold text-sm">
                        {{ signature.name.charAt(0).toUpperCase() }}
                      </span>
                    </div>
                    <div v-if="signature.imageRightsAgreed" class="text-green-500 dark:text-green-400">
                      <i class="fas fa-camera text-sm"></i>
                    </div>
                  </div>
                  
                  <h4 class="font-semibold text-gray-800 dark:text-white text-sm mb-1 truncate">
                    {{ signature.name }}
                  </h4>
                  <p class="text-xs text-gray-600 dark:text-gray-300 mb-1">
                    {{ signature.role }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {{ signature.sector }}
                  </p>
                  
                  <div class="flex items-center justify-between text-xs">
                    <span class="text-gray-500 dark:text-gray-400">
                      {{ formatTime(signature.timestamp) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal de Visualização de Assinatura -->
        <div v-if="showSignatureModal" class="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl font-bold text-gray-800 dark:text-white">
                <i class="fas fa-signature text-blue-600 dark:text-blue-400 mr-2"></i>
                Assinatura de {{ selectedSignature.name }}
              </h2>
              <button 
                @click="closeSignatureModal"
                class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <!-- Informações do Participante -->
            <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 mb-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-2">Nome Completo</label>
                  <p class="text-gray-800 dark:text-white text-lg">{{ selectedSignature.name }}</p>
                </div>
                <div>
                  <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-2">Cargo/Função</label>
                  <p class="text-gray-800 dark:text-white text-lg">{{ selectedSignature.role }}</p>
                </div>
                <div>
                  <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-2">Setor</label>
                  <p class="text-gray-800 dark:text-white text-lg">{{ selectedSignature.sector }}</p>
                </div>
                <div>
                  <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-2">Data e Hora da Assinatura</label>
                  <p class="text-gray-800 dark:text-white text-lg">{{ formatDateTime(selectedSignature.timestamp) }}</p>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-2">Direito de Imagem</label>
                  <div class="flex items-center">
                    <span v-if="selectedSignature.imageRightsAgreed" class="bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-3 py-1 rounded-full text-sm font-semibold">
                      <i class="fas fa-check mr-1"></i>
                      Autorizado
                    </span>
                    <span v-else class="bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-3 py-1 rounded-full text-sm font-semibold">
                      <i class="fas fa-times mr-1"></i>
                      Não Autorizado
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Visualização da Assinatura -->
            <div class="mb-6">
              <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-4">Assinatura Digital</label>
              <div class="border-2 border-gray-300 dark:border-gray-500 rounded-xl p-4 bg-gray-50 dark:bg-gray-700">
                <img 
                  v-if="selectedSignature.signature_data"
                  :src="selectedSignature.signature_data" 
                  alt="Assinatura digital"
                  class="w-full h-auto max-h-96 object-contain bg-white dark:bg-gray-600 rounded-lg shadow-sm"
                />
                <div v-else class="text-center py-12 text-gray-500 dark:text-gray-400">
                  <i class="fas fa-image text-4xl mb-2"></i>
                  <p>Assinatura não disponível</p>
                </div>
              </div>
            </div>

            <!-- Botões de Ação -->
            <div class="flex justify-end space-x-3">
              <button 
                @click="closeSignatureModal"
                class="bg-gray-500 dark:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors duration-300"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>

        <!-- Modal de Assinatura -->
        <div v-if="showSignatureInputModal" class="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 backdrop-blur-md flex items-center justify-center p-4 md:pl-24 md:pr-6" style="z-index: 999999 !important;">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative" style="z-index: 1000000 !important;">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl font-bold text-gray-800 dark:text-white">
                <i class="fas fa-pen text-blue-600 dark:text-blue-400 mr-2"></i>
                {{ isEditingMode ? 'Editar Assinatura' : 'Assinatura Digital' }} - {{ currentSignature.name }}
              </h2>
              <div class="text-right">
                <p class="text-sm text-gray-600 dark:text-gray-300">{{ currentSignature.role }}</p>
                <p class="text-sm text-gray-600 dark:text-gray-300">{{ currentSignature.sector }}</p>
              </div>
              <button 
                @click="closeSignatureInputModal"
                class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <!-- Canvas de Assinatura -->
            <div class="mb-6">
              <label class="block text-gray-700 dark:text-gray-200 font-semibold mb-4">Assine no espaço abaixo</label>
              <div class="border-2 border-dashed border-gray-300 dark:border-gray-500 rounded-xl p-4 bg-gray-50">
                <canvas
                  ref="signatureCanvas"
                  @mousedown="startDrawing"
                  @mousemove="draw"
                  @mouseup="stopDrawing"
                  @mouseleave="stopDrawing"
                  @touchstart="startDrawing"
                  @touchmove="draw"
                  @touchend="stopDrawing"
                  @touchcancel="stopDrawing"
                  class="w-full h-64 bg-white rounded border border-gray-300 dark:border-gray-500 cursor-crosshair mx-auto block touch-none"
                  width="800"
                  height="300"
                ></canvas>
                <div class="flex justify-between items-center mt-4">
                  <p class="text-sm text-gray-500 dark:text-gray-400">Use o dedo ou caneta para assinar</p>
                  <button 
  @click="clearSignature"
  class="flex items-center text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-semibold px-3 py-1 rounded border border-red-200 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900"
>
  <Trash2 class="w-4 h-4 mr-1" />
  Limpar
</button>
                </div>
              </div>
            </div>

            <!-- Botões de Ação -->
            <div class="flex justify-end space-x-3">
              <button 
                @click="closeSignatureInputModal"
                class="bg-gray-500 dark:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors duration-300"
              >
                Cancelar
              </button>
              <button 
                @click="confirmSignature"
                :disabled="!currentSignature.signature"
                class="bg-green-600 dark:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 dark:hover:bg-green-600 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i class="fas fa-check mr-2"></i>
                Confirmar Assinatura
              </button>
            </div>
          </div>
        </div>

        <!-- Modal de Confirmação para Finalizar Evento -->
        <div v-if="showFinishConfirmationModal" class="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <div class="text-center mb-6">
              <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 dark:bg-red-900 mb-4">
                <AlertTriangle class="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-2">Finalizar Evento</h2>
              <p class="text-gray-600 dark:text-gray-300">Você deseja encerrar este evento? Ele ficará marcado como concluído.</p>
            </div>
            
            <div class="flex space-x-3">
              <button 
                @click="closeFinishConfirmationModal"
                class="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 py-3 rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-300"
              >
                Cancelar
              </button>
              <button 
                @click="confirmFinishMeeting"
                :disabled="loading"
                class="flex-1 bg-red-600 dark:bg-red-500 text-white py-3 rounded-xl font-semibold hover:bg-red-700 dark:hover:bg-red-600 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check class="w-4 h-4 mr-2" />
                Sim, Finalizar
              </button>
            </div>
          </div>
        </div>

        <!-- Modal de Confirmação de Autorização de Imagem -->
        <div v-if="showImageRightsConfirmationModal" class="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <div class="text-center mb-6">
              <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900 mb-4">
                <AlertTriangle class="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-2">Autorização de Imagem</h2>
              <p class="text-gray-600 dark:text-gray-300">Você não marcou a autorização de uso de imagem. Tem certeza que deseja salvar a assinatura sem essa autorização?</p>
            </div>
            
            <div class="flex space-x-3">
              <button 
                @click="closeImageRightsConfirmationModal"
                class="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 py-3 rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-300"
              >
                Cancelar
              </button>
              <button 
                @click="confirmSaveWithoutImageRights"
                class="flex-1 bg-yellow-600 dark:bg-yellow-500 text-white py-3 rounded-xl font-semibold hover:bg-yellow-700 dark:hover:bg-yellow-600 transition-colors duration-300"
              >
                <Check class="w-4 h-4 mr-2 inline" />
                Sim, Salvar
              </button>
            </div>
          </div>
        </div>

      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import apiClient from '@/services/authService'
import { Plus, Printer, FolderOpen, Settings, X, Minimize2, Play, Trash2, AlertTriangle, Check } from 'lucide-vue-next'

// Estados principais
const currentView = ref('home')
const currentMeeting = ref(null)
const currentStep = ref(0)
const currentSignature = ref({ 
  name: '', 
  role: '', 
  sector: '', // Novo campo setor
  signature: '', 
  timestamp: null,
  imageRightsAgreed: false 
})
const selectedMeeting = ref(null)
const loading = ref(false)
const error = ref('')
const activeTab = ref('ongoing') // Nova aba ativa

// Estados para o modal de assinatura
const showSignatureModal = ref(false)
const selectedSignature = ref(null)
const showSignatureInputModal = ref(false) // Novo estado para o modal de assinatura

// Estado para o modal de confirmação de finalização
const showFinishConfirmationModal = ref(false)
const showNewMeetingModal = ref(false)

// Estado para o modal de confirmação de autorização de imagem
const showImageRightsConfirmationModal = ref(false)

// Estados para os dropdowns dos botões
const showPrinterDropdown = ref(false)
const showActionDropdown = ref(false)

const editingSignature = ref(null)
const isEditingMode = ref(false)

const meetingForm = ref({
  title: '',
  subject: '',
  instructor: '',
  date: new Date().toISOString().split('T')[0], // Data atual pré-definida
  time: '',
  location: '',
  workload: ''
})

// Dados das reuniões
const completedMeetings = ref([])
const ongoingMeetings = ref([]) // Reuniões em andamento
const isDrawing = ref(false)
const signatureCanvas = ref(null)

const steps = [
  {
    name: 'Lista de Presença',
    icon: 'fas fa-clipboard-list',
    description: 'Confirmação de presença no evento e autorização de uso de imagem.'
  }
]

const canSaveSignature = computed(() => {
  const nameValid = currentSignature.value.name.trim()
  const roleValid = currentSignature.value.role.trim()
  const sectorValid = currentSignature.value.sector.trim()
  const signatureValid = currentSignature.value.signature
  
  console.log('Validação da assinatura:', {
    name: nameValid,
    role: roleValid,
    sector: sectorValid,
    signature: signatureValid,
    imageRights: currentSignature.value.imageRightsAgreed
  })
  
  // Agora só exige os campos básicos, autorização de imagem é opcional
  return nameValid && roleValid && sectorValid && signatureValid
})

const isFormValid = computed(() => {
  return meetingForm.value.title.trim() &&  // Adicionar esta validação
         meetingForm.value.subject.trim() && 
         meetingForm.value.instructor.trim() && 
         meetingForm.value.date && 
         meetingForm.value.time && 
         meetingForm.value.location.trim()
})

// API Methods
const fetchMeetings = async () => {
  try {
    loading.value = true
    error.value = ''
    const response = await apiClient.get("/api/v1/meet/list")
    
    // Separa eventos finalizados e em andamento
    completedMeetings.value = response.data.filter(meeting => meeting.is_finalized)
    ongoingMeetings.value = response.data.filter(meeting => !meeting.is_finalized)
    
    console.log('Eventos carregados:', {
      finalizados: completedMeetings.value.length,
      emAndamento: ongoingMeetings.value.length
    })
  } catch (err) {
    error.value = 'Erro ao carregar eventos'
    console.error('Erro ao buscar eventos:', err)
  } finally {
    loading.value = false
  }
}

const createMeetingAPI = async (meetingData) => {
  try {
    loading.value = true
    error.value = ''
    const response = await apiClient.post('/api/v1/meet/', meetingData)
    return response.data
  } catch (err) {
    error.value = 'Erro ao criar evento'
    console.error('Erro ao criar evento:', err)
    throw err
  } finally {
    loading.value = false
  }
}

const saveSignatureAPI = async (meetingId, signatureData) => {
  try {
    loading.value = true
    error.value = ''
    const response = await apiClient.post(`/api/v1/meet/${meetingId}/signatures`, signatureData)
    return response.data
  } catch (err) {
    error.value = 'Erro ao salvar assinatura'
    console.error('Erro ao salvar assinatura:', err)
    throw err
  } finally {
    loading.value = false
  }
}

const updateSignatureAPI = async (signatureId, signatureData) => {
  try {
    console.log('Atualizando assinatura via API:', signatureId, signatureData)
    
    const response = await apiClient.put(`/api/v1/meet/signatures/${signatureId}`, signatureData)
    
    if (response.status === 200) {
      console.log('Assinatura atualizada com sucesso:', response.data)
      return response.data
    } else {
      throw new Error(`Erro HTTP: ${response.status}`)
    }
  } catch (err) {
    console.error('Erro ao atualizar assinatura:', err)
    
    if (err.response?.status === 404) {
      error.value = 'Assinatura não encontrada.'
    } else if (err.response?.status === 400) {
      error.value = 'Dados inválidos para atualização da assinatura.'
    } else {
      error.value = 'Erro ao atualizar assinatura. Tente novamente.'
    }
    
    throw err
  }
}

const getMeetingDetails = async (meetingId) => {
  try {
    loading.value = true
    error.value = ''
    const response = await apiClient.get(`/api/v1/meet/${meetingId}`)
    return response.data
  } catch (err) {
    error.value = 'Erro ao carregar detalhes do evento'
    console.error('Erro ao carregar detalhes do evento:', err)
    throw err
  } finally {
    loading.value = false
  }
}

// Métodos principais
const startNewMeeting = () => {
  meetingForm.value = {
    title: '',
    subject: '',
    instructor: '',
    date: new Date().toISOString().split('T')[0], // Sempre data atual
    time: '',
    location: '',
    workload: ''
  }
  showNewMeetingModal.value = true
}

const cancelNewMeeting = () => {
  meetingForm.value = {
    title: '',
    subject: '',
    instructor: '',
    date: new Date().toISOString().split('T')[0], // Mantém data atual
    time: '',
    location: '',
    workload: ''
  }
  showNewMeetingModal.value = false
}

const createMeeting = async () => {
  if (!isFormValid.value) return
  
  try {
    const meetingData = {
      title: meetingForm.value.title.trim(),
      subject: meetingForm.value.subject.trim(),
      instructor: meetingForm.value.instructor.trim(),
      date: meetingForm.value.date,
      time: meetingForm.value.time,
      location: meetingForm.value.location.trim(),
      workload: meetingForm.value.workload.trim() || ''
    }
    
    const createdMeeting = await createMeetingAPI(meetingData)
    
    // Fecha o modal
    showNewMeetingModal.value = false
    
    // O evento é criado com is_finalized = false (em andamento)
    currentMeeting.value = {
      ...createdMeeting,
      is_finalized: false,
      signatures: []
    }
    
    currentStep.value = 0
    currentView.value = 'signing'
    
    console.log('Novo evento criado e em andamento:', currentMeeting.value)
    
  } catch (err) {
    // Erro já tratado na função createMeetingAPI
  }
}

// Função removida - substituída por finalizeMeeting

const closeMeeting = async () => {
  if (!currentMeeting.value) return
  
  try {
    console.log('Fechando evento sem finalizar:', currentMeeting.value)
    
    // Volta para a tela inicial sem finalizar
    currentView.value = 'home'
    
    // Limpa o evento atual
    currentMeeting.value = null
    
    // Recarrega a lista de eventos
    await fetchMeetings()
    
    showSuccessMessage('Evento fechado. Você pode continuar depois.')
  } catch (err) {
    console.error('Erro ao fechar evento:', err)
    error.value = 'Erro ao fechar evento'
  }
}


const nextStep = () => {
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
    clearCurrentSignature()
    // Não precisa mais setupCanvas aqui, pois agora é feito no modal
  }
}

const saveSignature = async () => {
  console.log('Tentando salvar assinatura...')
  console.log('canSaveSignature:', canSaveSignature.value)
  console.log('currentMeeting:', currentMeeting.value)
  console.log('Modo de edição:', isEditingMode.value)
  
  if (!canSaveSignature.value || !currentMeeting.value) {
    console.log('Não pode salvar ou não há evento atual')
    return
  }
  
  // Verifica se a autorização de imagem não está marcada
  if (!currentSignature.value.imageRightsAgreed) {
    showImageRightsConfirmationModal.value = true
    return
  }
  
  // Se chegou aqui, pode prosseguir com o salvamento
  await proceedWithSaveSignature()
}

const proceedWithSaveSignature = async () => {
  
  try {
    const signatureData = {
      name: currentSignature.value.name.trim(),
      role: currentSignature.value.role.trim(),
      sector: currentSignature.value.sector.trim(),
      signature_data: currentSignature.value.signature,
      image_rights_agreed: currentSignature.value.imageRightsAgreed
    }
    
    console.log('Dados da assinatura para enviar:', signatureData)
    
    let savedSignature
    
    if (isEditingMode.value && editingSignature.value) {
      // Modo de edição - atualiza assinatura existente
      console.log('Atualizando assinatura existente:', editingSignature.value.id)
      savedSignature = await updateSignatureAPI(editingSignature.value.id, signatureData)
      
      // Atualiza a assinatura na lista local
      const signatureIndex = currentMeeting.value.signatures.findIndex(s => s.id === editingSignature.value.id)
      if (signatureIndex !== -1) {
        currentMeeting.value.signatures[signatureIndex] = {
          ...savedSignature,
          step: currentStep.value,
          stepName: steps[currentStep.value].name,
          timestamp: new Date(savedSignature.updated_at || savedSignature.created_at)
        }
      }
      
      showSuccessMessage('Assinatura atualizada com sucesso!')
    } else {
      // Modo de criação - nova assinatura
      savedSignature = await saveSignatureAPI(currentMeeting.value.id, signatureData)
      console.log('Assinatura salva com sucesso:', savedSignature)
      
      // Adiciona a assinatura localmente para feedback imediato
      const signature = {
        ...savedSignature,
        step: currentStep.value,
        stepName: steps[currentStep.value].name,
        timestamp: new Date(savedSignature.created_at)
      }
      
      if (!currentMeeting.value.signatures) {
        currentMeeting.value.signatures = []
      }
      currentMeeting.value.signatures.push(signature)
      
      showSuccessMessage('Assinatura salva com sucesso!')
    }
    
    // Limpa COMPLETAMENTE todos os campos para permitir nova pessoa assinar
    currentSignature.value = {
      name: '',
      role: '',
      sector: '',
      signature: '',
      imageRightsAgreed: false
    }
    
    // Limpa o canvas também
    if (signatureCanvas.value) {
      const canvas = signatureCanvas.value
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    
    // Reset do modo de edição
    isEditingMode.value = false
    editingSignature.value = null
    
    // Recarrega a lista de eventos para atualizar os contadores
    await fetchMeetings()
  } catch (err) {
    console.error('Erro ao salvar assinatura:', err)
    // Erro já tratado na função saveSignatureAPI
  }
}

// Métodos para o modal de confirmação de autorização de imagem
const closeImageRightsConfirmationModal = () => {
  showImageRightsConfirmationModal.value = false
}

const confirmSaveWithoutImageRights = async () => {
  showImageRightsConfirmationModal.value = false
  await proceedWithSaveSignature()
}

const clearCurrentSignature = () => {
  console.log('Limpando dados da assinatura atual')
  const currentSignatureData = currentSignature.value.signature // Preserva a assinatura
  
  currentSignature.value = { 
    name: '', 
    role: '', 
    sector: '', // Novo campo setor
    signature: currentSignatureData, // Mantém a assinatura existente
    timestamp: null,
    imageRightsAgreed: false 
  }
  // Não limpa mais o canvas aqui, pois agora é feito no modal
}

// Métodos de desenho
const setupCanvas = () => {
  console.log('Configurando canvas...')
  if (!signatureCanvas.value) {
    console.log('Canvas não encontrado')
    return
  }
  
  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')
  
  // Garante fundo branco no canvas
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  
  ctx.strokeStyle = '#000000' // Alterado para preto
  ctx.lineWidth = 3 // Aumentado para melhor visibilidade
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  
  // Desenha a linha guia
  drawGuideLine()
  
  console.log('Canvas configurado com sucesso')
}

// Função para obter coordenadas corretas do canvas
const getCanvasCoordinates = (e) => {
  const canvas = signatureCanvas.value
  const rect = canvas.getBoundingClientRect()
  
  // Calcula a escala do canvas
  const scaleX = canvas.width / rect.width
  const scaleY = canvas.height / rect.height
  
  let clientX, clientY
  
  if (e.touches && e.touches.length > 0) {
    // Touch event
    clientX = e.touches[0].clientX
    clientY = e.touches[0].clientY
  } else {
    // Mouse event
    clientX = e.clientX
    clientY = e.clientY
  }
  
  // Calcula as coordenadas relativas ao canvas
  const x = (clientX - rect.left) * scaleX
  const y = (clientY - rect.top) * scaleY
  
  return { x, y }
}

const startDrawing = (e) => {
  isDrawing.value = true
  const ctx = signatureCanvas.value.getContext('2d')
  const coords = getCanvasCoordinates(e)
  
  ctx.beginPath()
  ctx.moveTo(coords.x, coords.y)
  
  e.preventDefault()
}

const draw = (e) => {
  if (!isDrawing.value) return
  
  const ctx = signatureCanvas.value.getContext('2d')
  const coords = getCanvasCoordinates(e)
  
  ctx.lineTo(coords.x, coords.y)
  ctx.stroke()
  
  // Salva a assinatura como base64
  currentSignature.value.signature = signatureCanvas.value.toDataURL()
  console.log('Assinatura desenhada e salva:', currentSignature.value.signature ? 'Sim' : 'Não')
  
  e.preventDefault()
}

const stopDrawing = () => {
  isDrawing.value = false
}

const clearSignature = () => {
  if (!signatureCanvas.value) return
  
  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')
  
  // Limpa completamente o canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  
  // Garante fundo branco após limpar
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  
  // Redesenha a linha guia após limpar
  drawGuideLine()
  
  // Limpa a assinatura atual para permitir nova assinatura
  currentSignature.value.signature = ''
  
  // Reseta o estado de desenho
  isDrawing.value = false
  
  console.log('Assinatura limpa, canvas resetado e recalibrado')
}

const loadSignatureToCanvas = () => {
  if (!signatureCanvas.value || !currentSignature.value.signature) return
  
  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')
  
  // Limpa o canvas primeiro
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  
  // Garante fundo branco
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  
  // Redesenha a linha guia
  drawGuideLine()
  
  // Carrega a imagem da assinatura
  const img = new Image()
  img.onload = () => {
    ctx.drawImage(img, 0, 0)
  }
  img.src = currentSignature.value.signature
}

const drawGuideLine = () => {
  if (!signatureCanvas.value) return
  
  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')
  
  // Salva o estado atual do contexto
  ctx.save()
  
  // Configura a linha guia
  ctx.strokeStyle = '#000000' // Cor preta
  ctx.lineWidth = 1
  ctx.setLineDash([5, 5]) // Linha tracejada
  
  // Desenha a linha no meio do canvas (horizontalmente)
  const y = canvas.height / 2
  ctx.beginPath()
  ctx.moveTo(50, y) // Começa um pouco afastado da borda
  ctx.lineTo(canvas.width - 50, y) // Termina um pouco antes da borda
  ctx.stroke()
  
  // Restaura o estado do contexto
  ctx.restore()
}

// Métodos auxiliares
const getCurrentStepSignatures = () => {
  if (!currentMeeting.value || !currentMeeting.value.signatures) return []
  return currentMeeting.value.signatures.filter(sig => sig.step === currentStep.value)
}

const getStepSignatures = (meeting, stepIndex) => {
  if (!meeting || !meeting.signatures) return []
  return meeting.signatures.filter(sig => sig.step === stepIndex)
}

const viewMeeting = async (meeting) => {
  try {
    const meetingDetails = await getMeetingDetails(meeting.id)
    selectedMeeting.value = {
      ...meetingDetails,
      signatures: meetingDetails.signatures.map(sig => ({
        ...sig,
        step: 0, // Como temos apenas um step, todas as assinaturas são do step 0
        stepName: steps[0].name,
        timestamp: new Date(sig.created_at),
        imageRightsAgreed: sig.image_rights_agreed // Mapeia o campo do backend
      }))
    }
    currentView.value = 'viewMeeting'
  } catch (err) {
    // Erro já tratado na função getMeetingDetails
  }
}

const continueMeeting = async (meeting) => {
  try {
    console.log('Continuando evento:', meeting)
    
    // Carrega os detalhes do evento
    const meetingDetails = await getMeetingDetails(meeting.id)
    
    // Define o evento atual para continuar coletando assinaturas
    currentMeeting.value = {
      ...meetingDetails,
      signatures: meetingDetails.signatures.map(sig => ({
        ...sig,
        step: 0, // Como temos apenas um step, todas as assinaturas são do step 0
        stepName: steps[0].name,
        timestamp: new Date(sig.created_at),
        imageRightsAgreed: sig.image_rights_agreed // Mapeia o campo do backend
      })) || []
    }
    
    // Vai para a tela de assinaturas
    currentView.value = 'signing'
    currentStep.value = 0
    
    console.log('Evento carregado para continuar:', currentMeeting.value)
  } catch (err) {
    console.error('Erro ao continuar reunião:', err)
    error.value = 'Erro ao carregar evento para continuar'
  }
}



const closeSignatureModal = () => {
  showSignatureModal.value = false
  selectedSignature.value = null
}

const openSignatureInputModal = () => {
  console.log('Abrindo modal de assinatura...')
  showSignatureInputModal.value = true
  nextTick(() => {
    console.log('Modal aberto, configurando canvas...')
    setupCanvas()
    
    // Se já existe uma assinatura, carrega no canvas
    if (currentSignature.value.signature) {
      loadSignatureToCanvas()
    }
    
    // Adiciona listener para recalibrar o canvas quando a orientação mudar
    window.addEventListener('orientationchange', recalibrateCanvas)
    window.addEventListener('resize', recalibrateCanvas)
  })
}

const closeSignatureInputModal = () => {
  showSignatureInputModal.value = false
  
  // Remove os listeners de recalibração
  window.removeEventListener('orientationchange', recalibrateCanvas)
  window.removeEventListener('resize', recalibrateCanvas)
  
  // Não limpa a assinatura ao fechar, apenas o canvas visual
  // clearSignature() // Removido para preservar a assinatura
}

// Função para recalibrar o canvas
const recalibrateCanvas = () => {
  if (!signatureCanvas.value) return
  
  console.log('Recalibrando canvas...')
  
  // Aguarda um pouco para a mudança de orientação/resize terminar
  setTimeout(() => {
    if (signatureCanvas.value) {
      setupCanvas()
      
      // Se já existe uma assinatura, recarrega no canvas
      if (currentSignature.value.signature) {
        loadSignatureToCanvas()
      }
    }
  }, 300)
}

const confirmSignature = async () => {
  console.log('Confirmando assinatura:', currentSignature.value)
  console.log('Pode salvar?', canSaveSignature.value)
  
  if (currentSignature.value.signature) {
    // Fecha o modal para permitir salvar a assinatura
    closeSignatureInputModal()
    console.log('Modal fechado, assinatura pronta para salvar')
  } else {
    console.log('Não há assinatura para confirmar')
  }
}

// Métodos para o modal de confirmação de finalização
const openFinishConfirmationModal = () => {
  showFinishConfirmationModal.value = true
}

const closeFinishConfirmationModal = () => {
  showFinishConfirmationModal.value = false
}

const confirmFinishMeeting = async () => {
  try {
    loading.value = true
    error.value = ''
    
    console.log('Finalizando evento:', currentMeeting.value.id)
    
    // Chama a API para finalizar o evento no backend
    try {
      // Usa o endpoint específico de finalização (POST, não PUT)
      await apiClient.post(`/api/v1/meet/${currentMeeting.value.id}/finalize`)
      console.log('Evento finalizado no backend com sucesso')
    } catch (apiError) {
      console.error('Erro na API ao finalizar evento:', apiError)
      error.value = 'Erro ao finalizar evento no servidor. Tente novamente.'
      return
    }
    
    // Atualiza o evento atual como finalizado
    if (currentMeeting.value) {
      currentMeeting.value.is_finalized = true
      
      // Move o evento da lista de em andamento para a de concluídos
      const meetingIndex = ongoingMeetings.value.findIndex(m => m.id === currentMeeting.value.id)
      if (meetingIndex !== -1) {
        const finishedMeeting = ongoingMeetings.value.splice(meetingIndex, 1)[0]
        finishedMeeting.is_finalized = true
        completedMeetings.value.push(finishedMeeting)
      }
    }
    
    // Fecha o modal
    closeFinishConfirmationModal()
    
    // Volta para a tela inicial
    currentView.value = 'home'
    
    // Limpa o evento atual
    currentMeeting.value = null
    
    showSuccessMessage('Evento finalizado com sucesso!')
  } catch (err) {
    console.error('Erro ao finalizar evento:', err)
    error.value = 'Erro ao finalizar evento'
  } finally {
    loading.value = false
  }
}

// Método para gerar lista de presença
const generateAttendanceList = async () => {
  const meetingToUse = currentMeeting.value || selectedMeeting.value;
  if (!meetingToUse?.id) {
    showErrorMessage('Nenhuma reunião selecionada');
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const response = await fetch(`/api/v1/meet/${meetingToUse.id}/generate-attendance-list`, {
      method: 'GET',
      credentials: 'include'
    });

    if (response.ok) {
      // Verifica o tipo de conteúdo da resposta
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/pdf')) {
        // É um PDF válido
        const blob = await response.blob();
        downloadFile(blob, `Lista_Presenca_${meetingToUse.subject}_${new Date().toISOString().split('T')[0]}.pdf`);
        showSuccessMessage('Lista de presença gerada com sucesso!');
      } else {
        // Pode ser um erro em formato JSON
        try {
          const errorData = await response.json();
          showErrorMessage(errorData.error || 'Erro ao gerar PDF');
        } catch {
          showErrorMessage('Erro ao gerar PDF: formato de resposta inválido');
        }
      }
    } else {
      const errorData = await response.json();
      showErrorMessage(errorData.error || 'Erro ao gerar lista de presença');
    }
  } catch (err) {
    console.error('Erro ao gerar lista de presença:', err);
    showErrorMessage('Erro ao gerar lista de presença');
  } finally {
    loading.value = false;
  }
}

const generateImageAuthorizationTerm = async () => {
  const meetingToUse = currentMeeting.value || selectedMeeting.value;
  if (!meetingToUse?.id) {
    showErrorMessage('Nenhuma reunião selecionada');
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const response = await fetch(`/api/v1/meet/${meetingToUse.id}/generate-image-authorization-term`, {
      method: 'GET',
      credentials: 'include'
    });

    if (response.ok) {
      // Verifica o tipo de conteúdo da resposta
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/pdf')) {
        // É um PDF válido
        const blob = await response.blob();
        downloadFile(blob, `Termo_Autorizacao_Imagem_${meetingToUse.subject}_${new Date().toISOString().split('T')[0]}.pdf`);
        showSuccessMessage('Termo de autorização gerado com sucesso!');
      } else {
        // Pode ser um erro em formato JSON
        try {
          const errorData = await response.json();
          showErrorMessage(errorData.error || 'Erro ao gerar PDF');
        } catch {
          showErrorMessage('Erro ao gerar PDF: formato de resposta inválido');
        }
      }
    } else {
      const errorData = await response.json();
      showErrorMessage(errorData.error || 'Erro ao gerar termo de autorização');
    }
  } catch (err) {
    console.error('Erro ao gerar termo de autorização:', err);
    showErrorMessage('Erro ao gerar termo de autorização');
  } finally {
    loading.value = false;
  }
}

const formatDate = (date) => {
  if (!date) return 'Data não informada'
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  } catch (error) {
    console.error('Erro ao formatar data:', error)
    return 'Data inválida'
  }
}

const formatTime = (date) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDateTime = (date) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const showSuccessMessage = (message) => {
  // Cria uma notificação visual temporária
  const notification = document.createElement('div')
  notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform duration-300'
  notification.innerHTML = `
    <div class="flex items-center">
      <i class="fas fa-check-circle mr-2"></i>
      <span>${message}</span>
    </div>
  `
  
  document.body.appendChild(notification)
  
  // Anima a entrada
  setTimeout(() => {
    notification.classList.remove('translate-x-full')
  }, 100)
  
  // Remove após 3 segundos
  setTimeout(() => {
    notification.classList.add('translate-x-full')
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification)
      }
    }, 300)
  }, 3000)
}

const showErrorMessage = (message) => {
  // Cria uma notificação visual temporária de erro
  const notification = document.createElement('div')
  notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform duration-300'
  notification.innerHTML = `
    <div class="flex items-center">
      <i class="fas fa-exclamation-circle mr-2"></i>
      <span>${message}</span>
    </div>
  `
  
  document.body.appendChild(notification)
  
  // Anima a entrada
  setTimeout(() => {
    notification.classList.remove('translate-x-full')
  }, 100)
  
  // Remove após 5 segundos (erros ficam mais tempo visíveis)
  setTimeout(() => {
    notification.classList.add('translate-x-full')
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification)
      }
    }, 300)
  }, 5000)
}

// Função para download de arquivos compatível com dispositivos móveis
const downloadFile = (blob, filename) => {
  // Verifica se é um dispositivo móvel
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  
  if (isMobile) {
    // Para dispositivos móveis, abre em nova aba
    const url = window.URL.createObjectURL(blob)
    const newWindow = window.open(url, '_blank')
    
    // Se não conseguir abrir nova aba, tenta download direto
    if (!newWindow) {
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
    
    // Limpa o URL após um tempo
    setTimeout(() => {
      window.URL.revokeObjectURL(url)
    }, 1000)
  } else {
    // Para desktop, usa o método tradicional
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }
}



// Lifecycle
onMounted(() => {
  fetchMeetings()
  
  // Adiciona listener para fechar dropdowns quando clicar fora
  document.addEventListener('click', (e) => {
    // Verifica se o clique foi fora dos botões e dropdowns
    const printerButton = document.querySelector('[data-printer-button]')
    const actionButton = document.querySelector('[data-action-button]')
    
    if (printerButton && !printerButton.contains(e.target)) {
      showPrinterDropdown.value = false
    }
    
    if (actionButton && !actionButton.contains(e.target)) {
      showActionDropdown.value = false
    }
  })
})

// Métodos para controlar os dropdowns
const togglePrinterDropdown = () => {
  showPrinterDropdown.value = !showPrinterDropdown.value
  // Fecha o outro dropdown se estiver aberto
  if (showActionDropdown.value) {
    showActionDropdown.value = false
  }
}

const toggleActionDropdown = () => {
  showActionDropdown.value = !showActionDropdown.value
  // Fecha o outro dropdown se estiver aberto
  if (showPrinterDropdown.value) {
    showPrinterDropdown.value = false
  }
}



// Métodos modificados para fechar dropdowns após ação
const generateAttendanceListWithClose = async () => {
  await generateAttendanceList()
  showPrinterDropdown.value = false
}

const generateImageAuthorizationTermWithClose = async () => {
  await generateImageAuthorizationTerm()
  showPrinterDropdown.value = false
}

const openFinishConfirmationModalWithClose = () => {
  openFinishConfirmationModal()
  showActionDropdown.value = false
}

const closeMeetingWithClose = async () => {
  await closeMeeting()
  showActionDropdown.value = false
}
</script>
